import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '../api-service/api-service';
import { API } from '../../API';
import { Joueur, Pagination } from '../club-service/club-service';

export interface JoueursListResponse {
  success: boolean;
  data: Joueur[];
  total: number;
  pagination?: Pagination;
}

export interface Transfer {
  transfert_id: number;
  player_id: number;
  player_code: string;
  player_first_name: string;
  player_last_name: string;
  player_birthday?: string | null;
  player_gender?: string | null;
  category?: string | null;
  sportage_id?: number | null;
  sportage_name?: string | null;
  transfert_relation_id_old: number;
  transfert_club_id_old: number;
  old_club_id: number;
  old_club_code: string;
  old_club_name: string;
  old_club_short_code: string;
  logo_club_old: string | null;
  transfert_relation_id_new: number;
  transfert_club_id_new: number;
  new_club_id: number;
  new_club_code: string;
  new_club_name: string;
  new_club_short_code: string;
  logo_club_new: string | null;
  profile_link?: string | null;
  transfert_status: string;
  transfert_status_label: string;
  transfert_status_label_ara?: string;
  motif?: string | null;
  created_at: string;
  updated_at: string;
}

export interface FederationTransfersResponse {
  success: boolean;
  data: Transfer[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class PlayersService {
  private sourceListPage: string | null = null;

  constructor(private apiService: ApiService) {}

  setSourceListPage(page: string | null): void {
    this.sourceListPage = page;
  }

  getSourceListPage(): string | null {
    return this.sourceListPage;
  }

  // List players with pagination, search, and license status filtering
  listPlayers(clubId: number, page = 1, limit = 10, search = '', licenseStatuses?: string[], category?: string | null, seasonId?: number | null, demandeType?: string | null): Observable<JoueursListResponse> {
    const body = {
      club_id: clubId,
      page,
      limit,
      search,
      ...(licenseStatuses && { licenseStatuses }),
      ...(category ? { category } : {}),
      ...(seasonId ? { season_id: seasonId } : {}),
      ...(demandeType ? { demande_type: demandeType } : {})
    };
    return this.apiService.post(API.PLAYER.GET_PLAYERS, body);
  }

  listPlayersFederation(
    federationId: number,
    page = 1,
    limit = 10,
    search = '',
    licenseStatuses?: string[],
    clubId?: number | null,
    category?: string | null,
    seasonId?: number | null,
    demandeType?: string | null
  ): Observable<JoueursListResponse> {
    const body = {
      federation_id: federationId,
      page,
      limit,
      search,
      ...(licenseStatuses && { licenseStatuses }),
      ...(clubId ? { club_id: clubId } : {}),
      ...(category ? { category } : {}),
      ...(seasonId ? { season_id: seasonId } : {}),
      ...(demandeType ? { demande_type: demandeType } : {})
    };
    return this.apiService.post(API.PLAYER.GET_PLAYERS, body);
  }

  // Get single player details
  getPlayer(id: number, clubIdOrCle?: number | string | null, relationId?: number): Observable<{ success: boolean; data: Joueur }> {
    const body: any = {};
    if (typeof clubIdOrCle === 'number' && clubIdOrCle) {
      body.club_id = clubIdOrCle;
    } else if (typeof clubIdOrCle === 'string' && clubIdOrCle) {
      body.cle = clubIdOrCle;
    }
    let url = API.PLAYER.GET_PLAYER.replace(':id', id.toString());
    if (relationId) {
      url += `?relationId=${relationId}`;
    }
    return this.apiService.post(url, body);
  }

  // Update player relation status (ACTIVE/INACTIVE) — sends both status (desactivation_date) and relation_status_private (licence_status)
  updateRelationStatus(relationId: number, status: 'ACTIVE' | 'INACTIVE'): Observable<{ success: boolean; message: string }> {
    return this.apiService.post(API.PLAYER.UPDATE_STATUS.replace(':id', relationId.toString()), { status, relation_status_private: status });
  }

  // Get players statistics
  getStats(
    club_id: number,
    licenseStatuses?: string[],
    category?: string | null
  ): Observable<{ success: boolean; stats: { total: number; actifs: number; inactifs: number; total_hommes: number; total_femmes: number } }> {
    const body = {
      club_id,
      ...(licenseStatuses && { licenseStatuses }),
      ...(category ? { category } : {})
    };
    return this.apiService.post(API.PLAYER.GET_STATS, body);
  }

  getFederationStats(
    federation_id: number,
    licenseStatuses?: string[],
    category?: string | null
  ): Observable<{ success: boolean; stats: { total: number; total_hommes: number; total_femmes: number } }> {
    const body = {
      federation_id,
      ...(licenseStatuses && { licenseStatuses }),
      ...(category ? { category } : {})
    };
    return this.apiService.post(API.PLAYER.GET_STATS_FEDERATION, body);
  }

  // Get all stats for club views (anciens, nouveaux, transferts-vers, transferts-recus) in one call
  getStatsAll(
    club_id: number,
    category?: string | null
  ): Observable<{ success: boolean; stats: { anciens: any; nouveaux: any; transferts_vers: any; transferts_recus: any } }> {
    const body = {
      club_id,
      ...(category ? { category } : {})
    };
    return this.apiService.post(API.PLAYER.GET_STATS_ALL, body);
  }

  // Update player license status (licence_status in tab_player_club)
  updatePlayerStatus(
    relationId: number,
    licenseStatus: 'ACTIVE' | 'INACTIVE' | 'TRANSFERRED',
    relationStatus?: 'ACTIVE' | 'INACTIVE' | 'TRANSFERRED'
  ): Observable<{ success: boolean; message: string }> {
    const body: any = { relation_status_private: licenseStatus };
    if (relationStatus) {
      body.status = relationStatus;
    }
    return this.apiService.post(API.PLAYER.UPDATE_STATUS.replace(':id', relationId.toString()), body);
  }

  // Get player license status breakdown per federation
  getPlayerLicenseStats(
    federationId: number
  ): Observable<{ success: boolean; stats: { total: number; total_licnew: number; total_licnea: number; total_lirene: number; total_lirenan: number; total_liinac: number; total_flicval: number; total_flicrej: number } }> {
    const body = { federation_id: federationId };
    return this.apiService.post(API.PLAYER.GET_STATS_LICENSE, body);
  }

  // Get player age group stats (minors vs seniors)
  getPlayerAgeGroupStats(
    federationId: number
  ): Observable<{ success: boolean; stats: { total_mineurs: number; total_seniors: number } }> {
    const body = { federation_id: federationId };
    return this.apiService.post(API.PLAYER.GET_STATS_AGE_GROUPS, body);
  }

  // Get player category distribution
  getPlayerCategoryDistribution(federationId: number): Observable<{ success: boolean; data: any[] }> {
    const body = { federation_id: federationId };
    return this.apiService.post(API.PLAYER.GET_STATS_CATEGORIES, body);
  }

  // Get monthly license registrations
  getMonthlyLicenseRegistrations(federationId: number, seasonStart: string): Observable<{ success: boolean; data: any[] }> {
    const body = { federation_id: federationId, season_start: seasonStart };
    return this.apiService.post(API.PLAYER.GET_STATS_MONTHLY, body);
  }

  // Fetch licence PDF via file service for a given player_club relation
  getPlayerLicencePdf(playerId: number, relationId: number): Observable<{ success: boolean; data: any }> {
    const url = API.PLAYER.LICENCE_PDF
      .replace(':id', playerId.toString())
      .replace(':relationId', relationId.toString());
    return this.apiService.get(url);
  }

  listTransfersOut(clubId: number, page = 1, limit = 10, search = '', seasonId?: number | null): Observable<JoueursListResponse> {
    return this.apiService.post(API.PLAYER.TRANSFERS_OUT, { club_id: clubId, page, limit, search, ...(seasonId ? { season_id: seasonId } : {}) });
  }

  listTransfersIn(clubId: number, page = 1, limit = 10, search = '', seasonId?: number | null): Observable<JoueursListResponse> {
    return this.apiService.post(API.PLAYER.TRANSFERS_IN, { club_id: clubId, page, limit, search, ...(seasonId ? { season_id: seasonId } : {}) });
  }
}
